import { Component, ChangeDetectionStrategy, TemplateRef, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { GroupBase } from '../../../models/blueprint.model';
import { RecordUpdateEmission, ReportInfo, ReportRecord, ReportView } from '../../../models/report.model';
import { PropertyGroup, PropertyGroupQuery, TemplateField } from '../../../models/template.model';
import { ReportExportViewService } from '../../../report-export-view.service';
import { PropertyGroupService } from '../../../report-group.service';
import { IMAGE_FIELD_ID } from '../../../shared/templates/config/common';
import { LocaleChinaHkChineseEnglish } from '../../../shared/templates/utils/template-display-sort-order.const';
import { EditableFieldsService } from '../../editable-fields.service';
import { NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MiqImagesModule } from '@miq/utils';
import { L10nTranslationModule } from 'angular-l10n';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { ReportFieldFormatterPipe } from '../../../shared/pipes/report-field-formatter.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'compare-record-body',
  templateUrl: './compare-record-body.component.html',
  styleUrl: './compare-record-body.component.scss',
  imports: [
    MatIcon,
    CdkDragHandle,
    MiqImagesModule,
    L10nTranslationModule,
    MatFormField,
    MatSelect,
    FormsModule,
    MatOption,
    NgTemplateOutlet,
    NgClass,
    AsyncPipe,
    ReportFieldFormatterPipe
],
})
export class CompareRecordBodyComponent {
  public editableFieldsService = inject(EditableFieldsService);
  private readonly router = inject(Router);
  private readonly reportExportViewService = inject(ReportExportViewService);
  private readonly propertyGroupService = inject(PropertyGroupService);

  readonly confidentialityTmpl = input<TemplateRef<any>>();
  readonly allFields = input.required<TemplateField[]>();
  readonly allDataFields = input.required<TemplateField[]>();
  readonly record = input.required<ReportRecord>();
  readonly currentView = input.required<ReportView>();
  readonly groupsToDisplay = input.required<GroupBase<TemplateField>[]>();
  readonly order = input.required<number>();
  readonly recordList = input.required<any[]>();
  readonly reportData = input.required<ReportInfo>();
  readonly reportLocale = input.required<number>();
  readonly showGroup = input<boolean>(false);
  readonly propertyGroups = input<PropertyGroup[]>([]);
  readonly reportUpdateClick = output<RecordUpdateEmission>();
  readonly removeRecord = output<number>();

  imageField: TemplateField;
  isChina = false;
  selectedValue = 0;
  associationID: number;
  recordLevelGroups: PropertyGroup[] = [];

  constructor() {
    effect(() => {
      const groupsToDisplay = this.groupsToDisplay();
      const allFields = this.allFields();
      const reportLocale = this.reportLocale();
      const propertyGroups = this.propertyGroups();
      const record = this.record();

      this.editableFieldsService.mapEditableFields(groupsToDisplay);
      this.imageField = (allFields || []).find(field => field.id === IMAGE_FIELD_ID);
      if (LocaleChinaHkChineseEnglish.includes(reportLocale)) {
        this.isChina = true;
      }
      this.recordLevelGroups = [];
      if (propertyGroups && propertyGroups.length > 0 && record) {
        this.setGroupOptions();
      }
    });
  }

  emitEditableFields(event: MouseEvent, id: number) {
    const emittedObject = this.editableFieldsService.generateRecordUpdateEmission(event, id, this.record().data);
    if (emittedObject != null) {
      this.reportUpdateClick.emit(emittedObject);
    }
  }

  goToRecordDetails() {
    const { entity } = this.reportData();
    const currentRecordId = this.record().edpRecordId;
    const url = `/records/${entity}/${currentRecordId}/edit`;
    window.open(url, '_blank');
  }

  setGroupOptions() {
    const record = this.record();
    const propertyGroups = this.propertyGroups();
    let nextGroupName = 1;
    this.recordLevelGroups = propertyGroups
      .map(group => {
        const groupName = Number(group.groupName);
        nextGroupName = groupName >= nextGroupName ? groupName + 1 : nextGroupName;
        if (
          group.relatedRecordID === record.associatedPropertyId &&
          group.recordID === record.data.entity.id
        ) {
          this.selectedValue = group.groupID;
          this.associationID = group.associationID;
        }
        return { ...group, isDisabled: group.relatedRecordID !== record.associatedPropertyId };
      })
      .reduce(
        (uniqueGroup, groupItem) =>
          uniqueGroup.some(grp => grp.groupName === groupItem.groupName) ? uniqueGroup : [...uniqueGroup, groupItem],
        []
      );
    this.propertyGroupService.groupName = nextGroupName;
  }

  selectionChanged() {
    const record = this.record();
    if (this.selectedValue === 0) {
      // if selected value 0 then delete
      this.deleteGroupAssociation();
    } else if (this.selectedValue === -1) {
      // if selected value -1 then create new
      const query: PropertyGroupQuery = {
        recordID: record.data.entity.id,
        relatedRecordID: record.associatedPropertyId,
        groupName: this.propertyGroupService.groupName.toString(),
      };
      this.addUpdateGroupAssociation(query);
      this.propertyGroupService.groupName = this.propertyGroupService.groupName + 1;
    } else {
      //update
      const query: PropertyGroupQuery = {
        recordID: record.data.entity.id,
        relatedRecordID: record.associatedPropertyId,
        groupID: this.selectedValue,
      };
      this.addUpdateGroupAssociation(query);
    }
  }

  private addUpdateGroupAssociation(query: PropertyGroupQuery) {
    this.reportExportViewService
      .associatePropertyGroup(this.reportData().reportId, this.currentView().id, query)
      .subscribe((res: PropertyGroup[]) => {
        this.propertyGroupService.setReportPropertyGroups(res);
      });
  }

  private deleteGroupAssociation() {
    this.reportExportViewService
      .deletePropertyGroup(this.reportData().reportId, this.currentView().id, this.associationID)
      .subscribe((res: PropertyGroup[]) => {
        this.propertyGroupService.setReportPropertyGroups(res);
      });
  }
}
